import httpStatus from 'http-status';

import {
  IAuthHandler,
  JWTCookieHttpOnlyHandler,
  JWTStorageHandler,
} from '@/features/auth/services/AuthHandlers';
import AuthService from '@/features/auth/services/AuthService';
import { LoginResponse } from '@/features/auth/types/login';

import StoreManager from '@/business/StoreManager';
import { isHttpStatusError } from '@/utils/http';
import { AxiosError } from 'axios';
import { APIValidationError } from '@/types/Api';
import {
  AuthStatus,
  AuthStrategy,
  LoginInput,
  LoginPayload,
  LoginProvider,
} from './types';

type LoginErrorType = 'unknown' | 'invalid';

export class LoginError extends Error {
  public readonly code: LoginErrorType;

  constructor(error: Error) {
    let message = 'Impossível realizar o login';
    let code: LoginErrorType = 'unknown';
    const name = 'LoginError';

    if (isHttpStatusError(error, httpStatus.BAD_REQUEST)) {
      code = 'invalid';
      message =
        (error as AxiosError<APIValidationError<unknown>>)?.response?.data
          ?.non_field_errors?.[0] || 'Não foi possível realizar o login.';
    }

    super(message);
    this.name = name;
    this.code = code;

    Object.setPrototypeOf(this, new.target.prototype);
  }
}

export function isLoginError(err: unknown): err is LoginError {
  return err instanceof LoginError;
}

type LoginService = (payload: LoginPayload) => Promise<LoginResponse>;

type LoginServiceMappingType = {
  [provider in LoginProvider]: LoginService;
};

const LoginServiceMapping: LoginServiceMappingType = {
  email: AuthService.signIn as LoginService,
  apple: AuthService.signInWithApple as LoginService,
  google: AuthService.signInWithGoogle as LoginService,
  integrated: AuthService.signInWithAuthorizationCode as LoginService,
};

export function getAuthHandler(
  strategy: AuthStrategy,
  store: StoreManager,
): IAuthHandler {
  if (strategy === 'httponly') {
    return JWTCookieHttpOnlyHandler;
  }

  return JWTStorageHandler(store);
}

export async function doLogin({
  provider,
  payload,
}: LoginInput): Promise<LoginResponse> {
  const authLogin = LoginServiceMapping[provider];

  try {
    const data = await authLogin(payload);

    return data;
  } catch (err) {
    throw new LoginError(err as Error);
  }
}

export function getStore(strategy: AuthStrategy): StoreManager {
  return new StoreManager(
    strategy === 'session' ? sessionStorage : localStorage,
  );
}

export function isSessionExpired(status: AuthStatus): boolean {
  return ['cancelled', 'revoked'].includes(status);
}
